import { useState, useEffect } from "react"
import { useTareas } from "../context/TareasContext"
import Modal from "../../ui/Modal"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import { FaFolder, FaCheck, FaLock, FaUsers } from "react-icons/fa"
import InputFormValue from "../../ui/components/InputFormValue"
import MainSaveButton from "../../ui/components/MainSaveButton"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"

const COLORS = [
  "#3B82F6", "#10B981", "#F59E0B", "#EF4444",
  "#8B5CF6", "#EC4899", "#06B6D4", "#6B7280",
]

const ProjectCreateModal = () => {
  const user = useSelector(s => s.auth.user)
  const { createProjectModalOpen, setCreateProjectModalOpen, createProject, workers } = useTareas()

  const [form, setForm] = useState({
    name: "",
    description: "",
    color: "#3B82F6",
    createdBy: user?.id,
    visibility: "publico",
    viewers: [],
  })

  useEffect(() => {
    if (createProjectModalOpen) {
      setForm({
        name: "",
        description: "",
        color: "#3B82F6",
        createdBy: user?.id,
        visibility: "publico",
        viewers: [],
      })
    }
  }, [createProjectModalOpen, user?.id])

  const canEditVisibility =
    Boolean(user?.id && form.createdBy && String(user.id) === String(form.createdBy))

  const close = () => {
    setCreateProjectModalOpen(false)
  }

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      toast.warning("El nombre del proyecto es obligatorio")
      return
    }
    if (
      canEditVisibility &&
      form.visibility === "privado" &&
      (!form.viewers || form.viewers.length === 0)
    ) {
      toast.warning("En privado debes elegir al menos un trabajador con acceso.")
      return
    }
    try {
      await createProject({
        name: form.name.trim(),
        description: form.description?.trim() || "",
        color: form.color || "",
        createdBy: form.createdBy,
        visibility: canEditVisibility ? (form.visibility || "publico") : "publico",
        viewers:
          canEditVisibility && form.visibility === "privado" ? (form.viewers || []) : [],
      })
      close()
    } catch {
      // error ya manejado en context
    }
  }

  return (
    <Modal
      activeModal={createProjectModalOpen}
      title="Nuevo proyecto"
      onClose={close}
      centered={true}
      className="max-w-lg"
    >
      <div className="flex flex-col gap-5 text-xs">
        <InputFormValue
          required
          title="Nombre"
          value={form.name}
          setValue={v => setForm(p => ({ ...p, name: v }))}
          icon={<FaFolder />}
          placeholder="Nombre del proyecto"
          type="text"
        />

        <InputFormValue
          title="Descripción"
          value={form.description}
          setValue={v => setForm(p => ({ ...p, description: v }))}
          icon={<FaCheck />}
          placeholder="Descripción opcional..."
          type="textarea"
          rows={3}
        />

        {canEditVisibility && (
          <>
            <InputFormValue
              title="Visibilidad"
              type="select"
              value={form.visibility}
              setValue={(v) =>
                setForm((p) => ({
                  ...p,
                  visibility: v,
                  viewers: v === "publico" ? [] : p.viewers,
                }))
              }
              icon={<FaLock />}
              options={[
                { value: "publico", label: "Público (todos los trabajadores)" },
                { value: "privado", label: "Privado (solo quienes indiques)" },
              ]}
            />

            {form.visibility === "privado" && (
              <InputFormValue
                title="Quién puede ver"
                type="multiSelect"
                value={form.viewers}
                setValue={(viewers) => setForm((p) => ({ ...p, viewers }))}
                icon={<FaUsers />}
                placeholder="Marca a los trabajadores con acceso al proyecto"
                options={workers.map((w) => ({
                  value: w.id,
                  label: formatWorkerDisplayName(w.name || w.email) || w.id,
                }))}
                maxHeight="max-h-40"
              />
            )}
          </>
        )}

        <div>
          <label className="block text-gray-700 mb-2 font-medium">Color</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm(p => ({ ...p, color: c }))}
                className={`w-8 h-8 rounded-lg border-2 transition-all ${
                  form.color === c ? "border-gray-800 scale-110" : "border-transparent hover:scale-105"
                }`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
        </div>

        <MainSaveButton
          onClick={handleSubmit}
          fullWidth
          text="Crear proyecto"
        />
      </div>
    </Modal>
  )
}

export default ProjectCreateModal
